import React, { useState } from 'react';
import { X } from 'lucide-react';
import Button from '../common/Button';
import BadgePreview from './BadgePreview';

const TemplatePreviewModal = ({ isOpen, onClose, template, onSave }) => {
  const [showQR, setShowQR] = useState(true);
  const [showLogo, setShowLogo] = useState(true);
  const [sampleData, setSampleData] = useState({
    name: 'John Doe',
    company: 'Tech Corporation',
    title: 'Software Engineer',
    eventName: 'Tech Conference 2025',
  });

  const handleSave = () => {
    if (onSave) {
      onSave({
        layout: template?.value,
        showQR,
        showLogo,
      });
    }
    onClose();
  };

  if (!isOpen || !template) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">{template.label} Template</h3>
              <p className="text-sm text-gray-600 mt-1">{template.description}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Preview */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-4">Preview</h4>
                <div className="bg-gray-100 rounded-lg p-4 h-[500px]">
                  <BadgePreview
                    layout={template.value}
                    showQR={showQR}
                    showLogo={showLogo}
                    data={sampleData}
                  />
                </div>
              </div>

              {/* Settings */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-4">Settings</h4>
                <div className="space-y-6">
                  {/* Display Options */}
                  <div className="space-y-3">
                    <h5 className="text-sm font-medium text-gray-900">Display Options</h5>
                    <div className="space-y-2">
                      <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showQR}
                          onChange={(e) => setShowQR(e.target.checked)}
                          className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">Show QR Code</div>
                          <div className="text-xs text-gray-500">Display QR code for check-in</div>
                        </div>
                      </label>
                      <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={showLogo}
                          onChange={(e) => setShowLogo(e.target.checked)}
                          className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">Show Event Logo</div>
                          <div className="text-xs text-gray-500">Display event branding</div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Sample Data */}
                  <div className="space-y-3">
                    <h5 className="text-sm font-medium text-gray-900">Sample Data (for preview)</h5>
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Name"
                        value={sampleData.name}
                        onChange={(e) => setSampleData({ ...sampleData, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <input
                        type="text"
                        placeholder="Title"
                        value={sampleData.title}
                        onChange={(e) => setSampleData({ ...sampleData, title: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <input
                        type="text"
                        placeholder="Company"
                        value={sampleData.company}
                        onChange={(e) => setSampleData({ ...sampleData, company: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <input
                        type="text"
                        placeholder="Event Name"
                        value={sampleData.eventName}
                        onChange={(e) => setSampleData({ ...sampleData, eventName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  {/* Template Info */}
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h5 className="text-sm font-medium text-blue-900 mb-2">Template Features</h5>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Professional design</li>
                      <li>• Customizable colors and layout</li>
                      <li>• QR code integration</li>
                      <li>• Event branding support</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t bg-gray-50">
            <Button
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1"
            >
              Apply Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatePreviewModal;
